
import { defineComponent, ref, PropType, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { UUID } from '@Protium/vue-ozone-utils'
import { ContentType } from '@/services/directr'
import { format } from '@/utils/template'
import { DATE_DISPLAY_FORMAT_SHORT_HOURS } from '@/constants/index'
import Timeline from '@/components/Timeline.vue'
import Directr from '@/services/directr'
import TimelineNode from '@/components/TimelineNode.vue'
import DButton from '@/components/base/DButton.vue'
import EyeIcon from '@/components/Icons/EyeIcon.vue'
import { DisplayStatus, Status } from '@/models/status'

import { CtEvent, EventType } from '@/models/interfaces/content-type'
import { SortingDirection } from '@/utils'

const tempHeadings = {
  'shipped_first': 'Shipped to first forwarder',
  'arrived_first': 'Arrived at first forwarder',
  'shipped_second': 'Shipped to second forwarder',
  'arrived_second': 'Arrived at second forwarder',
  'shipped_dest': 'Shipped to destination',
  'arrived_dest': 'Arrived at destination',
  'replaced': 'Item has been replaced'
}
export default defineComponent({
  name: 'OrderEventTimeline',
  components: { EyeIcon, DButton, TimelineNode, Timeline },
  data () {
    return {

    }
  },
  props: {
    id: {
      type: String as PropType<UUID>,
      required: true
    },
    sticky: {
      type: Array as PropType<Array<EventType | number>>
    },
    direction: {
      type: String as PropType<SortingDirection>,
      default: SortingDirection.DESC
    }
  },
  setup (props) {
    const $store = useStore()
    const $directr: Directr = $store.getters['Services/Directr']
    const items = ref<Array<CtEvent>>([])

    watch(() => props.id, value => {
      void initTimeline().catch(console.error)
    })

    function initListeners () {
      if (!props.id) return
      $directr.listen(`create/event/${props.id}`, (payload) => {
        if (payload?.data && payload?.data?.sys?.type === 'event') items.value.push(payload?.data)
      })
    }

    async function initTimeline () {
      items.value = await $directr.get(ContentType.Event, [{ field: 'reference', method: 'is', match: props.id }], {}).then(({ items }) => items || [])
      items.value?.sort((a, b) => {
        return props.direction === SortingDirection.ASC
          ? new Date(a.sys.created_at as string).getTime() - new Date(b.sys.created_at as string).getTime()
          : -(new Date(a.sys.created_at as string).getTime() - new Date(b.sys.created_at as string).getTime())
      })
    }

    onMounted(() => {
      initListeners()
      void initTimeline().catch(console.error)
    })

    const timeline = computed(() => {
      return items.value?.map((item, i, a) => {
        const shouldStick = (props.sticky?.includes(item.type) || props.sticky?.includes(i))

        return {
          timestamp: item.sys.created_at,
          heading: item.label,
          type: styleMap[item.type] || styleMap[item.label],
          styles: { zIndex: shouldStick ? (a.length+1000) : (a.length - i), top: 0, background: 'var(--directr__lightest-blue)', position: shouldStick ? 'sticky' : undefined},
          subHeading: `${format.date(item.sys.created_at, DATE_DISPLAY_FORMAT_SHORT_HOURS)}`,
          loading: false,
          message: null,
          description: item.description,
          created_by: item.sys.created_by,
          meta: item.meta
        }
      })
    })

    const statusMap: { [key: string]: string } = {
      'Order created': Status.New,
      'Order accepted': Status.Final,
      'Order updated': Status.Updated,
      'Order reset': Status.New,
      'Order started picking': Status.Starting,
      'Order sourcing status updated': Status.Sourced,
      'Order picked': Status.Started,
      'Order picking': Status.Started,
      'Order started packing': Status.Active,
      'Order fulfilled': Status.Final,
      'Order put on-hold': Status.Paused,
      'Order rejected': Status.Failed,
      'Order Issue': Status.Failed
    }

    const styleMap: { [key: string]: string } = {
      [EventType.Success]: 'success',
      [EventType.Warn]: 'warning',
      [EventType.Info]: 'pending',
      [EventType.Error]: 'error',

      'Order created': 'success',
      'Order accepted': 'success',
      'Order updated': 'success',
      'Order reset': 'success',
      'Order started picking': 'pending',
      'Order sourcing': 'success',
      'Order picked': 'success',
      'Order picking': 'pending',
      'Order started packing': 'pending',
      'Order fulfilled': 'success',
      'Order put on-hold': 'warning',
      'Order rejected': 'warning',
      'Order Issue': 'error'
    }

    function getUsername (userId) {
      const user = $store.getters['User/UserDict'][userId]
      // if (!user) console.log('user',userId, $store.getters['User/UserDict'])
      return user?.label || 'unknown user'
    }

    return {
      Status,
      timeline,
      getUsername
    }
  }
})
